import { Link, MenuItem, SvgIcon, Tooltip, Typography, alpha } from '@mui/material';
import {
  ChevronUp as ExpandLessIcon,
  ChevronDown as ExpandMoreIcon,
  Star as StarIcon
} from '@opengov/react-capital-assets';
import React from 'react';

import { CounterAdornment } from '../Components/CounterAdornment';
import { NAV_COLOR_PALETTE, tooltipOffsetProps } from '../Components/styleConstants';

import { DATA_OG, DATA_TEST, getDynamicDataOGId } from '../enums';
import { IMenuOption, MuiSxProps } from '../types';
import { useCombinedRefs } from '../utils/hooks/useCombinedRefs';
import { useIsContentOverflowing } from '../utils/hooks/useIsContentOverflowing';
import { handleArrowDownToMoveFocus } from '../utils/keyboardNavUtils';

interface ILevelOneMenuItemProps {
  item: Partial<IMenuOption>;
  isSelected: boolean;
  firstLevelTwoItemRef?: React.MutableRefObject<any>;
  showExpandIcon?: boolean;
  dataTest?: string;
  styleOverrides?: MuiSxProps;
  showFavoriteIcon?: boolean;
  ariaControlsId?: string;
  isActive?: boolean;
  handleClick(): void;
}

interface IMenuItemChildrenProps {
  item: Partial<IMenuOption>;
  selected: boolean;
  favoriteIcon?: boolean;
  counterAdornment?: boolean;
  expandIcon?: boolean;
}

const MenuItemChildren: React.FC<IMenuItemChildrenProps> = ({
  favoriteIcon,
  item,
  counterAdornment,
  expandIcon,
  selected
}) => {
  const renderExpandIcon = (isSelected: boolean) => {
    if (isSelected)
      return <ExpandLessIcon data-test={DATA_TEST.expand_more_icon_selected} sx={{ fontSize: '1.25rem' }} />;
    return <ExpandMoreIcon data-test={DATA_TEST.expand_more_icon} sx={{ fontSize: '1.25rem' }} />;
  };
  return (
    <>
      {favoriteIcon && (
        <SvgIcon
          data-test={DATA_TEST.favorite_star_button_filled}
          sx={{ mr: 0.5, fontSize: '1rem', color: NAV_COLOR_PALETTE.favoriteStar }}
          component={StarIcon}
          inheritViewBox
        />
      )}
      <Typography noWrap={true} variant="h5" component="span">
        {item.label}
      </Typography>
      {counterAdornment && <CounterAdornment count={item.counterAdornmentValue} />}
      {expandIcon && renderExpandIcon(selected)}
    </>
  );
};

interface IMenuItemChildrenWrapperProps {
  children: React.ReactNode;
  item: Partial<IMenuOption>;
  selected: boolean;
  active?: boolean;
  styleOverrides?: MuiSxProps;
}

const MenuItemChildrenWrapper = React.forwardRef<HTMLSpanElement | HTMLAnchorElement, IMenuItemChildrenWrapperProps>(
  function Wrapper({ item, styleOverrides, active, children }, ref) {
    const wrapperStyles = {
      display: 'flex',
      alignItems: 'center',
      px: 1,
      pt: 0.5,
      color: 'inherit',
      height: '100%',
      borderBottom: '4px solid',
      borderBottomColor: active ? 'primary.main' : 'transparent'
    };

    const LabelWrapper = (
      <Typography
        ref={ref}
        data-test={DATA_TEST.level_one_item_label}
        variant="h5"
        component="span"
        sx={{
          ...wrapperStyles,
          ...styleOverrides
        }}
      >
        {children}
      </Typography>
    );

    const LinkWrapper = (
      <Link
        ref={ref}
        data-test={DATA_TEST.level_one_item_link}
        href={item.url}
        target={item.url && item.openInNewTab ? '_blank' : undefined}
        variant="h5"
        component={item.linkComponent || Link}
        underline="none"
        sx={{
          textDecoration: 'none',
          ':visited': {
            color: 'text.primary'
          },
          ...wrapperStyles,
          ...styleOverrides
        }}
        tabIndex={-1}
      >
        {children}
      </Link>
    );

    return item.url ? LinkWrapper : LabelWrapper;
  }
);

export const LevelOneMenuItem = React.forwardRef<HTMLLIElement, ILevelOneMenuItemProps>(function (
  {
    item,
    handleClick,
    isSelected,
    firstLevelTwoItemRef,
    showExpandIcon = true,
    dataTest,
    styleOverrides,
    showFavoriteIcon,
    ariaControlsId,
    isActive = false
  },
  ref
) {
  const labelRef = React.useRef<HTMLSpanElement | HTMLAnchorElement>(null);
  const showOverflowTooltip = useIsContentOverflowing(labelRef, item.label);
  const tooltipTitle = item.tooltipText || (showOverflowTooltip ? item.label : '');
  const menuItemRef = React.useRef<HTMLLIElement>(null);
  const combinedRef = useCombinedRefs(ref, menuItemRef);
  const isLeafNode = !showExpandIcon && !showFavoriteIcon;
  const showCounterAdornment = item.counterAdornmentValue !== undefined && item.counterAdornmentValue > 0;

  return (
    <Tooltip
      data-test={DATA_TEST.level_one_more_items_tooltip}
      title={tooltipTitle}
      enterNextDelay={500}
      placement="bottom"
      disableInteractive={true}
      {...tooltipOffsetProps}
      slotProps={{
        popper: {
          // Ensures that a warning does not appear in console in certain cases when menu item is removed from the DOM
          // Fixes: https://opengovinc.atlassian.net/browse/PSS-1446
          // Code based on: https://mui.com/material-ui/react-tooltip/#virtual-element
          anchorEl: {
            getBoundingClientRect: () => {
              const rect = menuItemRef.current!.getBoundingClientRect();
              return new DOMRect(rect.x + rect.width / 2, rect.y + rect.height);
            }
          }
        }
      }}
    >
      <MenuItem
        ref={combinedRef}
        id={item.id}
        aria-expanded={isSelected ? 'true' : undefined}
        aria-haspopup={isLeafNode ? 'false' : 'true'}
        aria-controls={ariaControlsId}
        data-test={dataTest ?? `level_one_item_${item.id}${item.url ? '_link' : ''}`}
        data-og={getDynamicDataOGId(DATA_OG.menu_item, item.label ?? '')}
        sx={{
          padding: 0,
          height: '100%',
          borderBottom: 'none',
          color: 'text.primary',
          backgroundColor: isSelected
            ? (theme) => theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.2)
              : theme.palette.primary.light
            : undefined,
          '&.Mui-focusVisible, &:focus-visible': {
            outline: (theme: any) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
            backgroundColor: 'transparent'
          },
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.12)
              : theme.palette.primary.light
          }
        }}
        onKeyDown={(e) => {
          if ([' ', 'Enter'].includes(e.key)) {
            if (item.url && labelRef?.current) {
              e.preventDefault();
              // If item has a URL, click inner Link component
              // (important for keyboard navigation & general accessibility)
              labelRef?.current?.click();
            }
          }
          handleArrowDownToMoveFocus(e.key, firstLevelTwoItemRef?.current);
        }}
        onClick={handleClick}
        tabIndex={0}
      >
        <MenuItemChildrenWrapper
          ref={labelRef}
          item={item}
          selected={isSelected}
          styleOverrides={{
            ...styleOverrides,
            ...(showExpandIcon && { pr: 0.25 })
          }}
          active={isActive}
        >
          <MenuItemChildren
            item={item}
            selected={isSelected}
            favoriteIcon={showFavoriteIcon}
            counterAdornment={showCounterAdornment}
            expandIcon={showExpandIcon}
          />
        </MenuItemChildrenWrapper>
      </MenuItem>
    </Tooltip>
  );
});
